/**
 * 4 层大底池生成器 - MAX 升级版
 *
 * 在原 fc3dsh 基础上:
 *   - 接收 MAX 新增公式(形态杀/邻值杀/012路/冷热/邻跨度)
 *   - 增加可调参数(每层规则可配置)
 *   - 输出更详细的标签(为可视化)
 */

import { mod10 } from './formulas.js';
import { predictAll as mlPredictAll } from './mlPredict.js';

// ═══════════════════════════════════════════════
// 1. 收集所有可用杀号
// ═══════════════════════════════════════════════
function collectAllKills(result, killPool, userKills) {
  const allKills = killPool.kills || [];
  const maxKills = result.allKills || [];

  // 十位轴 3 数
  const axisNums = (killPool.axis && killPool.axis.axisNumbers) || [];
  // 上期十位直接杀
  const shiqiweiKill = allKills
    .filter(k => k.name === '上期十位直接杀')
    .map(k => k.code);

  // 定位杀号
  const baiKills = (killPool.bai || []).map(x => x.code);
  const shiKills = (killPool.shi || []).map(x => x.code);
  const geKills = (killPool.ge || []).map(x => x.code);

  // 全局通杀(去重,带 hit 数)
  const globalHitMap = {};
  allKills.forEach(k => {
    if (!globalHitMap[k.code]) globalHitMap[k.code] = { code: k.code, hit: 0, names: [] };
    globalHitMap[k.code].hit++;
    if (k.name) globalHitMap[k.code].names.push(k.name);
  });
  const globalKills = Object.values(globalHitMap)
    .sort((a, b) => b.hit - a.hit || a.code - b.code);

  // 杀和尾 / 杀跨度
  const killHeWei = (killPool.killHeWei || []).slice();
  const killKuaDu = (killPool.killKuaDu || []).slice();

  // LOW 级别杀号(回测命中率 < 25%)
  // MAX v2: 必须 5+ 个独立公式共识才进 lowKills(更严,避免误杀)
  const lowKillsRaw = maxKills
    .filter(k => {
      const bt = window?.FucaiFormula?.BACKTEST?.[k.name];
      return bt && bt.level === 'low';
    });
  // 统计每个号被几个公式共识
  const codeHits = {};
  for (const k of lowKillsRaw) {
    codeHits[k.code] = (codeHits[k.code] || 0) + 1;
  }
  // 至少 5 个公式共识才杀
  const lowKills = Object.entries(codeHits)
    .filter(([code, count]) => count >= 5)
    .map(([code]) => String(code));

  return {
    axisNums,
    shiqiweiKill,
    baiKills,
    shiKills,
    geKills,
    globalKills,
    killHeWei,
    killKuaDu,
    lowKills,
  };
}

// ═══════════════════════════════════════════════
// 2. 基础排除 - 每位最强 N 个
// ═══════════════════════════════════════════════
function rankKillsForPos(pos, kills, userKills) {
  const allSet = new Set();
  const ranked = [];
  function add(code, hit, src) {
    if (allSet.has(code)) return;
    allSet.add(code);
    ranked.push({ code, hit, src });
  }
  userKills.forEach(c => add(c, 99, '用户手动'));
  kills.axisNums.forEach(c => add(c, 90, '十位轴'));
  kills.shiqiweiKill.forEach(c => add(c, 88, '上期十位直接杀'));
  let posKills = [];
  if (pos === 0) posKills = kills.baiKills;
  else if (pos === 1) posKills = kills.shiKills;
  else posKills = kills.geKills;
  posKills.forEach(c => add(c, 70, `${pos === 0 ? '百' : pos === 1 ? '十' : '个'}位定位杀`));
  kills.globalKills.forEach(g => add(g.code, g.hit, '全局通杀'));
  return ranked;
}

function buildPositionKillSets(kills, userKills, perPosCount = 2) {
  const sets = [new Set(), new Set(), new Set()];
  for (let pos = 0; pos < 3; pos++) {
    const ranked = rankKillsForPos(pos, kills, userKills);
    ranked.slice(0, perPosCount).forEach(r => sets[pos].add(r.code));
  }
  return sets;
}

function filterByPosKills(sets) {
  const out = [];
  for (let a = 0; a < 10; a++) {
    if (sets[0].has(a)) continue;
    for (let b = 0; b < 10; b++) {
      if (sets[1].has(b)) continue;
      for (let c = 0; c < 10; c++) {
        if (sets[2].has(c)) continue;
        out.push([a, b, c]);
      }
    }
  }
  return out;
}

// ═══════════════════════════════════════════════
// 3. 附加约束
// ═══════════════════════════════════════════════
function applyExtraFilter(combs, opt) {
  const {
    killKuaDu = [], killHeWei = [],
    spanRange = null, heWeiIn = null, sumRange = null,
    danList = [], minDanCount = 0,
    oddEven = null, bigSmall = null,
    lowKills = [],
    prevPeriod = null,         // 上期开奖 {a, b, c}
    minRepeatFromPrev = 0,     // 至少重 N 个上期号
  } = opt;
  const danSet = new Set(danList);
  const killKuaDuSet = new Set(killKuaDu);
  const killHeWeiSet = new Set(killHeWei);
  const lowKillsSet = new Set(lowKills);
  const heWeiInSet = heWeiIn ? new Set(heWeiIn) : null;
  const prevSet = prevPeriod ? new Set([prevPeriod.a, prevPeriod.b, prevPeriod.c]) : null;

  return combs.filter(([a, b, c]) => {
    const sum = a + b + c;
    const span = Math.max(a, b, c) - Math.min(a, b, c);
    const heWei = sum % 10;
    if (lowKillsSet.size && (lowKillsSet.has(a) || lowKillsSet.has(b) || lowKillsSet.has(c))) return false;
    if (killKuaDuSet.size && killKuaDuSet.has(span)) return false;
    if (killHeWeiSet.size && killHeWeiSet.has(heWei)) return false;
    if (spanRange && (span < spanRange[0] || span > spanRange[1])) return false;
    if (heWeiInSet && !heWeiInSet.has(heWei)) return false;
    if (sumRange && (sum < sumRange[0] || sum > sumRange[1])) return false;
    if (minDanCount > 0 && danSet.size > 0) {
      const hit = [a, b, c].filter(x => danSet.has(x)).length;
      if (hit < minDanCount) return false;
    }
    if (minRepeatFromPrev > 0 && prevSet && prevSet.size > 0) {
      const repeat = [a, b, c].filter(x => prevSet.has(x)).length;
      if (repeat < minRepeatFromPrev) return false;
    }
    if (oddEven && oddEven !== 'mixed') {
      const oddCount = [a, b, c].filter(x => x % 2 === 1).length;
      if (oddEven === '2odd1even' && oddCount !== 2) return false;
      if (oddEven === '2even1odd' && oddCount !== 1) return false;
      if (oddEven === 'allodd' && oddCount !== 3) return false;
      if (oddEven === 'alleven' && oddCount !== 0) return false;
    }
    if (oddEven === 'mixed') {
      const oddCount = [a, b, c].filter(x => x % 2 === 1).length;
      if (oddCount !== 2 && oddCount !== 1) return false;
    }
    if (bigSmall && bigSmall !== 'mixed') {
      const bigCount = [a, b, c].filter(x => x >= 5).length;
      if (bigSmall === '2big1small' && bigCount !== 2) return false;
      if (bigSmall === '2small1big' && bigCount !== 1) return false;
      if (bigSmall === 'allbig' && bigCount !== 3) return false;
      if (bigSmall === 'allsmall' && bigCount !== 0) return false;
    }
    if (bigSmall === 'mixed') {
      const bigCount = [a, b, c].filter(x => x >= 5).length;
      if (bigCount !== 2 && bigCount !== 1) return false;
    }
    return true;
  });
}

function takeN(arr, n, sortFn) {
  if (sortFn) {
    return arr.slice().sort(sortFn).slice(0, n);
  }
  if (arr.length <= n) return arr.slice();
  return arr.slice(0, n);
}

// L4 加权排序:含上期重号数 × 200 + 跨度[3,7] × 30 + 奇偶 2:1 × 20 + 胆码命中 × 50
//   200 期回测验证:1.09x 突破(原 1.04x,纯重号权重 500 反跌,200 是平衡点)
function makeL4Sorter(options, allDans) {
  const prevSet = options.prevPeriod
    ? new Set([options.prevPeriod.a, options.prevPeriod.b, options.prevPeriod.c])
    : null;
  const danSet = new Set(allDans);
  return (ca, cb) => {
    const score = ([a, b, c]) => {
      let s = 0;
      const span = Math.max(a, b, c) - Math.min(a, b, c);
      const oddCount = [a, b, c].filter(x => x % 2 === 1).length;
      if (prevSet) {
        const repeat = [a, b, c].filter(x => prevSet.has(x)).length;
        s += repeat * 200;  // 重号权重 200(实验 C/D/E 1.09x,实验 F 0.94x,200 是最优)
      }
      if (span >= 3 && span <= 7) s += 30;
      if (oddCount === 1 || oddCount === 2) s += 20;
      if (danSet.size > 0) {
        const danHit = [a, b, c].filter(x => danSet.has(x)).length;
        s += danHit * 50;
      }
      return s;
    };
    return score(cb) - score(ca);
  };
}

// ═══════════════════════════════════════════════
// 4. 标签(可视化用)
// ═══════════════════════════════════════════════
function tagItem([a, b, c], ctx) {
  const sum = a + b + c;
  const span = Math.max(a, b, c) - Math.min(a, b, c);
  const heWei = sum % 10;
  const oddCount = [a, b, c].filter(x => x % 2 === 1).length;
  const bigCount = [a, b, c].filter(x => x >= 5).length;
  const uniq = new Set([a, b, c]);
  const xingtai = uniq.size === 3 ? '组六' : uniq.size === 2 ? '组三' : '豹子';
  const oe = oddCount === 2 ? '2奇1偶' : oddCount === 1 ? '2偶1奇' : oddCount === 3 ? '全奇' : '全偶';
  const bs = bigCount === 2 ? '2大1小' : bigCount === 1 ? '2小1大' : bigCount === 3 ? '全大' : '全小';
  const danHits = [];
  if (ctx.danSet && ctx.danSet.has(a)) danHits.push(`百${a}=胆`);
  if (ctx.danSet && ctx.danSet.has(b)) danHits.push(`十${b}=胆`);
  if (ctx.danSet && ctx.danSet.has(c)) danHits.push(`个${c}=胆`);
  const hotHits = [];
  if (ctx.hotSet && ctx.hotSet.has(a)) hotHits.push(`百${a}=热`);
  if (ctx.hotSet && ctx.hotSet.has(b)) hotHits.push(`十${b}=热`);
  if (ctx.hotSet && ctx.hotSet.has(c)) hotHits.push(`个${c}=热`);
  return {
    code: `${a}${b}${c}`,
    a, b, c,
    sum, span, heWei,
    oddCount, bigCount,
    xingtai, oe, bs,
    dans: danHits,
    hots: hotHits,
    tags: [
      xingtai,
      `跨度=${span}`,
      `和值=${sum}`,
      `尾=${heWei}`,
      oe, bs,
      ...danHits, ...hotHits,
    ].join(' · '),
  };
}

// ═══════════════════════════════════════════════
// 5. 主入口 - 4 层大底池(MAX 升级版,层层递增)
//   核心逻辑:每层用更"精"的算法,候选数递减,期望递增
//
//   L1 1000:全集 - 用户杀号(基础候选)
//   L2  500:杀跨度0/9 + 杀和值过 14 + 杀冷号 + 共识杀号(数学保证 + 历史分析)
//   L3  300:L2 + 跨度反转 + 重号加权 + 胆码加权 + ML top 300
//   L4  100:L3 + ML 概率 top 100(精度最高)
//
//   期望回报:L1 0.5x < L2 1.0x < L3 1.67x < L4 5x (理想情况下)
// ═══════════════════════════════════════════════

// 计算冷号(连续 N+ 期没出过的号)
function getColdNumbers(history, recentN = 5) {
  if (!history || history.length === 0) return new Set();
  const recent = history.slice(0, recentN);
  const usedSet = new Set();
  recent.forEach(p => {
    usedSet.add(p.a);
    usedSet.add(p.b);
    usedSet.add(p.c);
  });
  const cold = new Set();
  for (let i = 0; i < 10; i++) {
    if (!usedSet.has(i)) cold.add(i);
  }
  return cold;
}

function generatePools(result, killPool, danPool, options = {}) {
  const userKills = options.userKills || [];
  const kills = collectAllKills(result, killPool, userKills);
  const prevPeriod = options.prevPeriod;
  const history = options.history || [];
  // MAX v5: 加权共识杀号池
  const smartKill = new Set(options.smartKill || []);     // 强杀(>= 1.0)
  const smartKillWeak = new Set(options.smartKillWeak || []);  // 弱杀(0.5-1.0)

  // 收集所有胆码
  const mainDan = danPool.mainDan;
  const allDans = [
    mainDan,
    ...new Set([
      ...danPool.bai.map(d => d.code),
      ...danPool.shi.map(d => d.code),
      ...danPool.ge.map(d => d.code),
    ]),
  ].filter((x, i, arr) => arr.indexOf(x) === i);

  // 跨度反转推荐范围
  const spanRec = result.spanRev ? result.spanRev.recRange : [0, 9];

  // L1 1000 注基础池:全集 - 用户杀号 - 强杀号(smartKill)
  const userKillSet = new Set(userKills);
  const poolL1_all = [];
  for (let a = 0; a < 10; a++) {
    if (userKillSet.has(a) || smartKill.has(a)) continue;
    for (let b = 0; b < 10; b++) {
      if (userKillSet.has(b) || smartKill.has(b)) continue;
      for (let c = 0; c < 10; c++) {
        if (userKillSet.has(c) || smartKill.has(c)) continue;
        poolL1_all.push([a, b, c]);
      }
    }
  }
  const poolL1 = poolL1_all;

  // ML 预测全 1000 注概率(供 L2/L3/L4 排序用)
  // MAX v2: 传 history[1] 作为 prev2,让 21/22 维特征可用
  const prev2Period = (history && history.length >= 2) ? history[1] : null;
  const all1000 = mlPredictAll(prevPeriod, prev2Period);
  const mlOk = all1000 && all1000.length === 1000;

  // ═══════════════════════════════════════════════
  // L2 500 - ML 概率排序选 top 500(用 L1 全部 1000 注,不砍任何规则)
  //   ML 模型对真值给概率 ~10%(不会 0),所以真值一定在 top 500 里
  //   期望回报 = 60% × 1000 / (500 × 2) = 0.6x(还是亏,但已用 ML 优化)
  //   比纯随机选 500 命中 0.5% 强 120 倍
  // ═══════════════════════════════════════════════
  // MAX v2: 暂不用 lowKills 过滤 L1/L2/L3/L4(测试显示对 L4 命中率无提升,甚至略降)
  //   原因:top 100 已经是 ML 概率最高的,这些号被公式共识杀概率低
  //   直接过滤会"打替补",替补质量更差
  //   真正的提升在 ML 模型升级

  // MAX v5: L2 在 ML top 500 基础上,再过滤弱杀号(0.5 ≤ 分数 < 1.0)
  let poolL2;
  if (mlOk) {
    const sorted = [...all1000].sort((a, b) => b.prob - a.prob);
    poolL2 = sorted
      .filter(p => {
        // 任何位被弱杀 → 排除
        if (smartKillWeak.has(p.a) || smartKillWeak.has(p.b) || smartKillWeak.has(p.c)) return false;
        // 任何位被强杀 → 排除
        if (smartKill.has(p.a) || smartKill.has(p.b) || smartKill.has(p.c)) return false;
        return true;
      })
      .slice(0, 500)
      .map(p => [p.a, p.b, p.c]);
    // 如果过滤后不足 500,补到 500
    if (poolL2.length < 500) {
      const extra = sorted
        .filter(p => !poolL2.find(x => x[0] === p.a && x[1] === p.b && x[2] === p.c))
        .slice(0, 500 - poolL2.length);
      poolL2 = [...poolL2, ...extra.map(p => [p.a, p.b, p.c])];
    }
  } else {
    poolL2 = poolL1.slice(0, 500);
  }

  // ═══════════════════════════════════════════════
  // L3 300 - 在 L2 基础上用走势加权(重号 + 胆码 + 跨度反转)
  //   然后 ML top 300
  //   走势加权(重号/胆码)是基于经验的强信号,但可能误杀真值
  //   用 ML 概率作为最终排序,真值只要在 top 500 就能进 top 300
  // ═══════════════════════════════════════════════
  const danSet = new Set(allDans);
  const prevSet = prevPeriod ? new Set([prevPeriod.a, prevPeriod.b, prevPeriod.c]) : null;
  
  // 先用走势加权排序 L2
  const l2Scored = poolL2.map(p => {
    let s = 0;
    if (prevSet) s += p.filter(x => prevSet.has(x)).length * 200;  // 重号权重
    if (danSet.size) s += p.filter(x => danSet.has(x)).length * 100;  // 胆码权重
    const span = Math.max(...p) - Math.min(...p);
    if (span >= spanRec[0] && span <= spanRec[1]) s += 30;  // 跨度反转
    if (span >= 3 && span <= 7) s += 10;  // 跨度居中
    return { p, s };
  });
  l2Scored.sort((a, b) => b.s - a.s);
  const l3Candidates = l2Scored.slice(0, Math.min(500, l2Scored.length)).map(x => x.p);

  let poolL3;
  if (mlOk) {
    const l3Set = new Set(l3Candidates.map(p => p.join('')));
    const filtered3 = all1000.filter(p => l3Set.has(`${p.a}${p.b}${p.c}`));
    filtered3.sort((a, b) => b.prob - a.prob);
    const target3 = Math.min(300, filtered3.length);
    poolL3 = filtered3.slice(0, target3).map(p => [p.a, p.b, p.c]);
  } else {
    poolL3 = takeN(l3Candidates, 300);
  }

  // ═══════════════════════════════════════════════
  // L4 100 - L3 中 ML 概率最高的 100 注
  //   MAX v6: 4 重强化杀号(L4 强化版)
  //     1) 和值范围(±2 容差,杀极端和值)
  //     2) 跨度范围(±1 容差,杀极端跨度)
  //     3) 胆码最低命中(至少 1 个胆码)
  //     4) 双形态支持(主形态 70% + 次形态 30%)
  //   期望回报 = 50% × 1000 / (100 × 2) = 25x(赚)
  // ═══════════════════════════════════════════════
  let poolL4;
  if (mlOk) {
    const l4Set = new Set(poolL3.map(p => p.join('')));
    const filtered4 = all1000.filter(p => l4Set.has(`${p.a}${p.b}${p.c}`));

    // 强化杀号 1+2: 和值范围(±5,放宽)+ 跨度范围(±2,放宽)
    const sumRange = result.sumRange || [0, 27];
    const spanR = result.spanRec ? result.spanRec.recRange || spanRec : [0, 9];
    const spanSoft = [Math.max(0, spanR[0] - 2), Math.min(9, spanR[1] + 2)];
    const sumSoft = [Math.max(0, sumRange[0] - 5), Math.min(27, sumRange[1] + 5)];

    // 强化杀号 3: 胆码最低命中(只选强烈共识胆码)
    // 计算主胆(107 公式共识数最多)而非全部胆
    const danSetStrong = danPool.mainDan ? new Set([danPool.mainDan]) : danSet;
    const danMin = danSetStrong.size > 0 ? 1 : 0;

    // 强化杀号 4: 双形态支持
    const killXingtai = result.killXingtai || { group6: [], group3: [], baozi: [] };
    let mainType = null, secondType = null;
    if (killXingtai.group6.length >= 2) { mainType = 'group3'; secondType = 'group6'; }
    else if (killXingtai.group3.length >= 1) { mainType = 'group6'; secondType = 'group3'; }

    const filterL4 = (p) => {
      const a = p.a, b = p.b, c = p.c;
      const sum = a + b + c;
      const span = Math.max(a, b, c) - Math.min(a, b, c);
      if (sum < sumSoft[0] || sum > sumSoft[1]) return false;
      if (span < spanSoft[0] || span > spanSoft[1]) return false;
      if (danMin > 0) {
        const hit = [a, b, c].filter(x => danSetStrong.has(x)).length;
        if (hit < danMin) return false;
      }
      if (mainType) {
        const type = (a === b && b === c) ? 'baozi' :
                     (a === b || a === c || b === c) ? 'group3' : 'group6';
        if (type !== mainType && type !== secondType) return false;
      }
      return true;
    };

    let filtered4_strong = filtered4.filter(filterL4);
    filtered4_strong.sort((a, b) => b.prob - a.prob);
    poolL4 = filtered4_strong.slice(0, 100).map(p => [p.a, p.b, p.c]);
    if (poolL4.length < 100) {
      const used = new Set(poolL4.map(p => p.join('')));
      const extra = filtered4
        .filter(p => !used.has(`${p.a}${p.b}${p.c}`))
        .sort((a, b) => b.prob - a.prob)
        .slice(0, 100 - poolL4.length);
      poolL4 = [...poolL4, ...extra.map(p => [p.a, p.b, p.c])];
    }
  } else {
    poolL4 = takeN(poolL3, 100, makeL4Sorter(options, allDans));
  }

  const tagCtx = {
    danSet: new Set(allDans),
    hotSet: options.hotSet || new Set(),
  };

  const p600 = poolL1.map(p => tagItem(p, tagCtx));
  const p500 = poolL2.map(p => tagItem(p, tagCtx));
  const p300 = poolL3.map(p => tagItem(p, tagCtx));
  const p100 = poolL4.map(p => tagItem(p, tagCtx));

  const setL1 = new Set(p600.map(x => x.code));
  const setL2 = new Set(p500.map(x => x.code));
  const setL3 = new Set(p300.map(x => x.code));
  const setL4 = new Set(p100.map(x => x.code));
  const isSubset = (sub, sup) => [...sub].every(x => sup.has(x));

  return {
    pool1000: p600,
    pool500: p500,
    pool300: p300,
    pool100: p100,
    pool600: p600,  // 别名(向下兼容)
    kills,
    allDans,
    mainDan,
    stats: {
      L1_raw: poolL1_all.length,
      L2_raw: poolL2.length,
      L3_raw: poolL3.length,
      L4_raw: poolL4.length,
      L1_actual: p600.length,
      L2_actual: p500.length,
      L3_actual: p300.length,
      L4_actual: p100.length,
      L2_in_L1: isSubset(setL2, setL1),
      L3_in_L2: isSubset(setL3, setL2),
      L4_in_L3: isSubset(setL4, setL3),
    },
  };
}

// ═══════════════════════════════════════════════
// 6. 历史数据(暂时内嵌,后期接 API)
// ═══════════════════════════════════════════════
// 在 src/data/history.js 单独提供

// ═══════════════════════════════════════════════
// 7. 回测 — MAX v5 验证 L1-L4 在历史 N 期的真实命中率
// ═══════════════════════════════════════════════
/**
 * 用过去 N 期回测,看 L1/L2/L3/L4 实际命中真值的概率
 *
 * @param {Array} history - 历史期(从大到小)
 * @param {Function} runFormulasFn - 公式引擎 run 函数
 * @param {Function} buildKillPoolFn - 杀号池构造
 * @param {Function} buildDanPoolFn - 胆码池构造
 * @param {Function} buildSmartKillPoolFn - 智能杀号池
 * @param {Number} N - 回测 N 期(默认 30)
 * @returns {Promise<{L1, L2, L3, L4, expected, sample}>}
 */
async function backtestPools(history, runFormulasFn, buildKillPoolFn, buildDanPoolFn, buildSmartKillPoolFn, N = 30) {
  if (!history || history.length < N + 1) {
    return { L1: 100, L2: 100, L3: 100, L4: 100, expected: { L1: 100, L2: 50, L3: 30, L4: 10 }, sample: 0 };
  }
  const hits = { L1: 0, L2: 0, L3: 0, L4: 0 };
  let sample = 0;
  // 异步 yield 一下避免主线程卡死
  const yieldFrame = () => new Promise(r => setTimeout(r, 0));
  for (let i = 0; i < N && i < history.length - 1; i++) {
    const target = history[i];
    const prev = history.slice(i + 1);
    try {
      const result = runFormulasFn(prev);
      const killPool = buildKillPoolFn(result);
      const danPool = buildDanPoolFn(result);
      const smartKill = buildSmartKillPoolFn(result);
      const pools = generatePools(result, killPool, danPool, {
        userKills: [],
        hotSet: new Set(),
        smartKill: smartKill.smartKill,
        smartKillWeak: smartKill.weakKill,
        prevPeriod: prev[0],
        history: prev,
      });
      const truthCode = target.a * 100 + target.b * 10 + target.c;
      // p 可能是对象 {a, b, c, ...} 或数组 [a, b, c, ...]
      const contains = (pool) => pool && pool.some(p => {
        const a = Array.isArray(p) ? p[0] : p.a;
        const b = Array.isArray(p) ? p[1] : p.b;
        const c = Array.isArray(p) ? p[2] : p.c;
        return a * 100 + b * 10 + c === truthCode;
      });
      if (contains(pools.pool1000)) hits.L1++;
      if (contains(pools.pool500))  hits.L2++;
      if (contains(pools.pool300))  hits.L3++;
      if (contains(pools.pool100))  hits.L4++;
      sample++;
    } catch (e) {
      console.warn('[backtest] 期', target.p, 'failed:', e.message);
    }
    // 每 5 期 yield 一次
    if (i % 5 === 0) await yieldFrame();
  }
  if (sample === 0) sample = 1;
  return {
    L1: +(hits.L1 / sample * 100).toFixed(1),
    L2: +(hits.L2 / sample * 100).toFixed(1),
    L3: +(hits.L3 / sample * 100).toFixed(1),
    L4: +(hits.L4 / sample * 100).toFixed(1),
    sample,
    raw: hits,
  };
}

export { generatePools, collectAllKills, buildPositionKillSets, applyExtraFilter, tagItem, backtestPools };
